<template>
    <div class="pa-4">
        <v-card class="mt-2 py-2">
            <v-toolbar flat>
                <v-toolbar-title>Tester - View Only</v-toolbar-title>
                <v-divider class="mx-4" inset vertical />
                <v-spacer />
                <v-btn color="grey" dark class="mb-2 mr-2" @click="backToTester" small>
                    <v-icon small>mdi-arrow-left</v-icon>
                    Back
                </v-btn>
            </v-toolbar>
            <div class="mt-3">
                <v-row class="mx-5">
                    <v-col lg="2" md="3" sm="4" cols="4">Delivery Date</v-col>
                    <v-col lg="2" md="3" sm="8" cols="8">
                        <v-skeleton-loader type="text" v-if="isLoading" />
                        <span v-else>{{ formattedDate }}</span>
                    </v-col>
                    <v-col lg="2" md="3" sm="4" cols="4">Volume</v-col>
                    <v-col lg="2" md="3" sm="8" cols="8">
                        <v-skeleton-loader type="text" v-if="isLoading" />
                        <span v-else>{{ selectedBottleDescription }}</span>
                    </v-col>
                </v-row>
                <v-row class="mx-5">
                    <v-col lg="2" md="3" sm="4" cols="4">Coordinator</v-col>
                    <v-col lg="2" md="3" sm="8" cols="8">
                        <v-skeleton-loader type="text" v-if="isLoadingCoordinator || isLoading" />
                        <span v-else>{{ coordinatorName }}</span>
                    </v-col>
                    <v-col lg="2" md="3" sm="4" cols="4">Expired Date</v-col>
                    <v-col lg="2" md="3" sm="8" cols="8">
                        <v-skeleton-loader type="text" v-if="isLoading" />
                        <span v-else>{{ expiredDate }}</span>
                    </v-col>
                    <v-col lg="2">
                        <v-btn depressed color="green" class="d-inline ms-2 white--text" @click="export2xls">Export to XLS</v-btn>
                    </v-col>
                </v-row>
                <v-row class="compact-form my-2 mx-1">
                    <v-col>
                        <v-card outlined>
                            <v-skeleton-loader v-if="isLoading"
                                v-bind="attrs"
                                type="table-heading, list-item-two-line, table-tfoot"
                                ></v-skeleton-loader>
                            <v-simple-table v-else class="spreadsheet-table flex-table" fixed-header height="400" ref="tabelExport">
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="header-cell text-center" width="10">No</th>
                                            <th class="header-cell text-center">Code</th>
                                            <th class="header-cell text-center">Product</th>
                                            <th class="header-cell text-center">Ambil Stok Adelphi</th>
                                            <th class="header-cell text-center">Sisa</th>
                                            <th class="header-cell text-center" v-for="(outlet, ido) in outlets" :key="ido">
                                                {{ outlet.outlet_name }}
                                            </th>
                                            <th class="header-cell text-center">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(product, idp) in filteredItems" :key="product.product_code">
                                            <td class="header-cell text-center">{{ idp + 1 }}</td>
                                            <td class="header-cell text-no-wrap">{{ product.product_code }}</td>
                                            <td class="header-cell text-no-wrap">{{ product.product_name }} ({{ product.product_size }} ML)</td>
                                            <td class="spreadsheet-cell text-center">
                                                <span>{{ product.quantity_from_adelphi }}</span>
                                            </td>
                                            <td class="spreadsheet-cell text-center" :class="{'warning-input': isWarning(product) }">
                                                <span>{{ calculateSisa(product) }}</span>
                                            </td>
                                            <td class="spreadsheet-cell text-center" v-for="(outlet, ido) in product.outlets" :key="ido">
                                                <span>{{ outlet.quantity }}</span>
                                            </td>
                                            <td class="spreadsheet-cell text-center">
                                                <span>{{ calculateTotal(product) }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th colspan="3" class="header-text text-right">Jumlah Total</th>
                                            <th class="spreadsheet-cell text-center">
                                                <span>{{ totalQtyStokAdelphi }}</span>
                                            </th>
                                            <th class="spreadsheet-cell text-center">
                                                <span>{{ totalQtySisa }}</span>
                                            </th>

                                            <th class="spreadsheet-cell text-center" v-for="(outlet, ido) in outlets" :key="ido">
                                                <span>{{ totalQtyOutlet(outlet.outlet_code) }}</span>
                                            </th>

                                            <th class="spreadsheet-cell text-center">
                                                <span>{{ totalQtyTotal }}</span>
                                            </th>
                                        </tr>
                                    </tfoot>
                                </template>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </v-card>
    </div>
</template>

<script>
import moment from 'moment';
import { utils, writeFileXLSX } from 'xlsx';

export default {
    data() {
        return {
            isLoading: false,
            isLoadingCoordinator: false,
            date: '',
            expiredDate: '',
            selectedCoordinator: '',
            coordinators: [],
            items: [],
            details: [],
            outlets: [],
            products: [],
            bottles: [],
            selectedBottle: '250',
            attrs: {
                class: 'mb-6',
                boilerplate: true,
                elevation: 2,
            },
        }
    },
    
    computed: {
        filteredItems() {
            return this.items.filter(item => item.product_size === this.selectedBottle);
        },
        totalQtyStokAdelphi() {
            return this.filteredItems.reduce((total, item) => total + Number(item.quantity_from_adelphi), 0);
        },
        totalQtySisa() {
            return this.filteredItems.reduce((total, item) => total + Number(this.calculateSisa(item)), 0);
        },
        totalQtyTotal() {
            return this.filteredItems.reduce((total, item) => total + Number(this.calculateTotal(item)), 0);
        },
        coordinatorName() {
            const coordinator = this.coordinators.find(c => c.code === this.selectedCoordinator);
            return coordinator ? coordinator.name : '';
        },
        selectedBottleDescription() {
            const bottle = this.bottles.find(b => b.code === this.selectedBottle);
            return bottle ? bottle.description : '';
        },
        formattedDate() {
            if (!this.date) return '';
            return moment(this.date).format('DD-MMM-YYYY');
        }
    },

    async created() {
        this.isLoadingCoordinator = true;
        this.isLoading = true;

        await this.getDataProducts();
        this.coordinators = (await this.$store.dispatch('coordinator/getObjs')).data.data;
        this.isLoadingCoordinator = false;

        this.getBottles();

        await this.fetchDetails();
    },

    methods: {
        backToTester() {
            this.$router.push('/tester');
        },
        getBottles() {
            let dataMiscCodes = this.$store.state.misccode.objs;
            this.bottles = dataMiscCodes.filter((i => i.type.includes('Botol')))
                .sort((a,b) => {
                    return parseInt(a.code) - parseInt(b.code)
                });
        },
        async getDataProducts() {
            let response = await this.$store.dispatch('variant/getObjs')
            if (response.data.status == 'success') {
                this.products = response.data.data;
                this.products = this.products.filter(p => p.is_active == 1)
                    .sort((a, b) => a.sort_no - b.sort_no);
            }
        },
        async fetchDetails() {
            this.isLoading = true;

            try {
                const paramId = this.$route.params.id;                
                const response = (await this.$store.dispatch('tester/getDetails', {
                    id: paramId,
                    date: this.date,
                    coordinator: this.selectedCoordinator
                }));

                const data = response.data.data;
                this.date = data.date;
                this.expiredDate = data.expired_date;
                const allOutlets = [
                    ...data.outlets,
                    ...data.additional_outlets
                ];
                this.outlets = allOutlets;
                this.details = data.details;
                this.selectedCoordinator = data.coordinator_code;
                
                this.items = this.products.map(product => ({
                    product_code: product.code,
                    product_name: product.name,
                    product_size: product.size,
                    quantity_from_adelphi: this.qtyFromAdelphi(product.code),
                    outlets: this.qtyOutlet(product.code),
                }));
            } catch (error) {
                console.log('Error fetching tester detail:', error);
            } finally {
                this.isLoading = false;
            }
        },
        qtyFromAdelphi(productCode) {
            let result = 0;
            const detailSelected = this.details.find((product) => product.product_code == productCode);
            if (detailSelected) {
                result = detailSelected.quantity_from_adelphi;
            }

            return result;
        },
        qtyOutlet(productCode) {
            let result = [];

            if (this.outlets.length > 0) {
                this.outlets.forEach((outlet) => {
                    result.push({
                        outlet_code: outlet.outlet_code,
                        quantity: this.getQtyOutlet(outlet.outlet_code, productCode),
                    });
                })
            }

            return result;
        },
        getQtyOutlet(outletCode, productCode) {
            let result = 0;
            const detailSelected = this.details.find((product) => product.product_code == productCode 
                && product.outlet_code == outletCode);

            if (detailSelected) {
                result = detailSelected.quantity;
            }

            return result;
        },
        totalQtyOutlet(outletCode) {
            let result = 0;

            this.filteredItems.forEach((item) => {
                item.outlets.forEach((outlet) => {
                    if (outlet.outlet_code == outletCode) {
                        result += Number(outlet.quantity);
                    }
                });
            });

            return result;
        },
        calculateSisa(product) {
            let result = 0;

            result = Number(product.quantity_from_adelphi) - this.calculateTotal(product);

            return result;
        },
        calculateTotal(product) {
            let result = 0;

            if (product.outlets) {
                product.outlets.forEach((outlet) => {
                    result += Number(outlet.quantity);
                });
            }

            return result;
        },
        isWarning(product) {
            const result = this.calculateSisa(product);
            return result != 0 ? true : false;
        },
        export2xls() {
            const wb = utils.table_to_book(this.$refs.tabelExport.$el.firstChild.firstChild);
            writeFileXLSX(wb, "Tester.xlsx");
        }
    }
}
</script>

<style scoped>
.spreadsheet-table {
  border-collapse: collapse;
  width: 100%;
}

.header-cell,
.spreadsheet-cell {
  border: 1px solid #ddd;
  padding: 8px !important;
  text-align: left;
  min-width: 100px;
}

.spreadsheet-cell {
  background-color: #f9f9f9;
  padding: 8px !important;
}

.view {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
}

.table-container {
  display: flex;
  margin-top: 20px;
  flex-grow: 1;
  overflow: hidden;
}

.flex-table {
  display: flex;
  flex-grow: 1;
  width: 100%;
}

.flex-table > div {
  width: 100%;
}

.warning-input {
  background-color: #FBDADE !important;
}

.v-data-table table tfoot th
{
	position: sticky;
	bottom: 0;
	background: #FFF;
}
</style>
